<script setup>
import { ref, watch } from 'vue'
import { Button, Drawer, Spinner, Stat } from '@drama/ui'
import { Eye } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

import { getUsageCall } from '../../../api/usage.js'
import { formatDateTime, formatDuration, formatMoney } from '../llm-format.js'
import { videoSupplierCostText } from '../../../lib/video-supplier-cost.js'
import LongText from '../../../components/LongText.vue'
import VideoCallPayload from './VideoCallPayload.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  callId: { type: String, required: true },
  capability: { type: String, required: true },
})
const emit = defineEmits(['update:open', 'preview'])
const { t } = useI18n()
const detail = ref(null)
const loading = ref(false)
const error = ref('')

watch(
  () => [props.open, props.capability, props.callId],
  () => {
    if (props.open) load()
  },
  { immediate: true },
)

async function load() {
  loading.value = true
  error.value = ''
  detail.value = null
  try {
    detail.value = (await getUsageCall(props.capability, props.callId)).detail
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Drawer
    :open="open"
    width="min(920px, 92vw)"
    :title="t('visualGameAdmin.llm.calls.detail.title', { id: callId })"
    @update:open="emit('update:open', $event)"
  >
    <div class="capability-call">
      <div v-if="loading" class="capability-call__state"><Spinner /></div>
      <p v-else-if="error" class="vga-error">{{ error }}</p>
      <div v-else-if="detail" class="capability-call__content">
        <div class="capability-call__stats">
          <Stat :label="t('visualGameAdmin.llm.calls.table.status')" :value="t(`visualGameAdmin.llm.calls.status.${detail.call.status}`)" />
          <Stat :label="t('visualGameAdmin.llm.calls.table.duration')" :value="detail.call.duration_ms === null ? '—' : formatDuration(detail.call.duration_ms)" />
          <Stat :label="t('visualGameAdmin.llm.calls.table.cost')" :value="detail.call.capability === 'video' ? videoSupplierCostText(detail.call, t) : detail.call.supplier_cost_micros === null ? '—' : formatMoney(detail.call.supplier_cost_micros)" />
          <Stat :label="t('visualGameAdmin.llm.calls.detail.createdAt')" :value="formatDateTime(detail.call.created_at)" />
        </div>

        <section class="capability-call__section">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.identifiers') }}</h3>
          <dl class="capability-call__facts">
            <div>
              <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.provider') }}</dt>
              <dd>{{ detail.call.provider_display_name }} · {{ detail.call.provider_capability_code }}</dd>
            </div>
            <div><dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.model') }}</dt><dd>{{ detail.call.model }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.llm.calls.detail.callId') }}</dt><dd>{{ detail.call.id }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.llm.calls.detail.providerRequestId') }}</dt><dd>{{ detail.call.provider_request_id ?? '—' }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.llm.calls.detail.providerTaskId') }}</dt><dd>{{ detail.call.provider_task_id ?? '—' }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.llm.calls.detail.ledgerId') }}</dt><dd>{{ detail.call.ledger_id ?? '—' }}</dd></div>
          </dl>
          <Button
            v-if="detail.call.output_url"
            class="capability-call__preview"
            variant="secondary"
            size="sm"
            @click="emit('preview', detail.call)"
          >
            <Eye :size="15" />
            {{ t('visualGameAdmin.llm.calls.detail.preview') }}
          </Button>
        </section>

        <VideoCallPayload
          v-if="detail.call.capability === 'video'"
          :request="detail.request"
          :response="detail.response"
          :provider="detail.call.provider"
          :status="detail.call.status"
          :total-tokens="detail.call.total_tokens"
          :output-url="detail.call.output_url"
          :start-frame-url="detail.call.start_frame_url"
          :end-frame-url="detail.call.end_frame_url"
          :input-presentations="detail.video_input_presentations"
        />
        <section v-else-if="detail.call.capability === 'embedding'" class="capability-call__section">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.response') }}</h3>
          <LongText :label="t('visualGameAdmin.llm.calls.detail.response')" :text="JSON.stringify(detail.response, null, 2)" :copy-text="JSON.stringify(detail.response, null, 2)" />
        </section>
        <template v-else>
          <section class="capability-call__section">
            <h3>{{ t('visualGameAdmin.llm.calls.detail.request') }}</h3>
            <LongText :label="t('visualGameAdmin.llm.calls.detail.request')" :text="JSON.stringify(detail.request, null, 2)" :copy-text="JSON.stringify(detail.request, null, 2)" />
          </section>
          <section class="capability-call__section">
            <h3>{{ t('visualGameAdmin.llm.calls.detail.response') }}</h3>
            <LongText :label="t('visualGameAdmin.llm.calls.detail.response')" :text="JSON.stringify(detail.response, null, 2)" :copy-text="JSON.stringify(detail.response, null, 2)" />
          </section>
        </template>
        <section class="capability-call__section">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.accounting') }}</h3>
          <pre>{{ JSON.stringify({ pricing_snapshot: detail.pricing_snapshot, billing_policy_snapshot: detail.billing_policy_snapshot }, null, 2) }}</pre>
        </section>
        <section v-if="detail.provider_fields" class="capability-call__section">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.providerFields') }}</h3>
          <pre>{{ JSON.stringify(detail.provider_fields, null, 2) }}</pre>
        </section>
      </div>
    </div>
  </Drawer>
</template>

<style scoped lang="scss">
.capability-call {
  &__content {
    display: grid;
    gap: var(--drama-space-xl);
  }

  &__state {
    display: grid;
    place-items: center;
    min-height: 240px;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--drama-space-md);
  }

  &__section {
    display: grid;
    gap: var(--drama-space-md);

    h3 {
      margin: 0;
    }

    pre {
      max-height: 420px;
      margin: 0;
      padding: var(--drama-space-md);
      overflow: auto;
      border: 1px solid var(--drama-color-border);
      border-radius: var(--drama-radius-sm);
      background: var(--drama-color-surface-alt);
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--drama-space-md);
    margin: 0;

    div {
      display: grid;
      gap: var(--drama-space-xs);
    }

    dt {
      color: var(--drama-color-text-muted);
      font-size: var(--drama-font-size-xs);
    }

    dd {
      margin: 0;
      overflow-wrap: anywhere;
    }
  }

  &__preview {
    justify-self: start;
  }

  @media (max-width: 760px) {
    &__stats,
    &__facts {
      grid-template-columns: 1fr;
    }
  }
}
</style>
